import * as React from 'react';
import { Label as AriaLabel } from 'react-aria-components';

import { HelpCircleIcon } from '../../icons';
import { cx } from '../../utils/cx';
import Tooltip, { TooltipTrigger } from '../Tooltip';

import type { LabelProps as AriaLabelProps } from 'react-aria-components';

interface ILabelProps extends AriaLabelProps {
  children: React.ReactNode;
  isRequired?: boolean;
  tooltip?: string;
  tooltipDescription?: string;
  ref?: React.Ref<HTMLLabelElement>;
}

const Label = ({ isRequired, tooltip, tooltipDescription, className, ...props }: ILabelProps) => {
  return (
    <AriaLabel
      // Used for conditionally hiding/showing the label element via CSS:
      // <Input label="Visible only on mobile" className="lg:**:data-label:hidden" />
      // or
      // <Input label="Visible only on mobile" className="lg:label:hidden" />
      data-label="true"
      {...props}
      className={cx('text-secondary flex cursor-default items-center gap-0.5 text-sm font-medium', className)}
    >
      {props.children}

      <span
        className={cx(
          'text-brand-tertiary hidden',
          isRequired && 'block',
          typeof isRequired === 'undefined' && 'group-required:block'
        )}
      >
        *
      </span>

      {tooltip && (
        <Tooltip title={tooltip} description={tooltipDescription} placement="top">
          <TooltipTrigger
            // `TooltipTrigger` inherits the disabled state from the parent form field
            // but we don't that. We want the tooltip be enabled even if the parent
            // field is disabled.
            isDisabled={false}
            className="text-fg-quaternary hover:text-fg-quaternary_hover focus:text-fg-quaternary_hover cursor-pointer transition duration-200"
          >
            <HelpCircleIcon className="size-4" />
          </TooltipTrigger>
        </Tooltip>
      )}
    </AriaLabel>
  );
};
Label.displayName = 'Label';

export default Label;
